import type { Column } from "@tanstack/react-table";
import { cva } from "class-variance-authority";
import { type ClassValue, clsx } from "clsx";
import {
	addDays,
	addMonths,
	differenceInCalendarMonths,
	format,
	parseISO,
} from "date-fns";
import type { CSSProperties } from "react";
import { twMerge } from "tailwind-merge";
import { getDomain as parseDomain } from "tldts";
import { ContactFormCategory } from "../components/help-and-support/contact-form/contactForm.type";
import { Origin } from "../components/sites/site/overview/metrics.types";
import { PromoCodeType } from "../components/sites/site/rates-and-packages/promo-code/promoCode.types";
import {
	AccessRate,
	AvailableType,
	Deposits,
	LOSDiscountsType,
	type Rate,
	type SpecificDaysRate,
} from "../components/sites/site/rates-and-packages/rates/rate.types";
import { CancellationReason } from "../components/sites/site/reservation/reservations.type";
import { AcceptedCards } from "../components/sites/site/site-customization/manage-site/advanced-tab/payment.types";
import type { Option } from "../components/ui/multi-select";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const BUTTON_VARIANTS = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-[10px] text-sm font-bold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline:
					"border border-color bg-background hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
				accent: "bg-accent text-color hover:bg-accent/50 ]",
				input:
					"flex !h-9 w-full rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-5 font-normal",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export const badgeVariants = cva(
	"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs capitalize font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
	{
		variants: {
			variant: {
				default:
					"border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
				secondary:
					"border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
				destructive:
					"border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
				outline: "text-secondary bg-secondary/20 rounded-md px-2 py-1",
				success: "bg-[#00DD3E26] text-primary px-2",
				danger: "bg-[#B9010126] text-destructive px-2",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export const getCommonPinningStyles = <T>(
	column: Column<T>,
	isHeader = false,
	isRowSelected = false,
): CSSProperties => {
	const isPinned = column.getIsPinned();
	const isLastLeftPinnedColumn =
		isPinned === "left" && column.getIsLastColumn("left");
	const isFirstRightPinnedColumn =
		isPinned === "right" && column.getIsFirstColumn("right");

	return {
		boxShadow: isLastLeftPinnedColumn
			? ""
			: isFirstRightPinnedColumn
				? ""
				: undefined,
		left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
		right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
		backgroundColor: isHeader
			? "hsl(var(--secondary))"
			: isRowSelected && !isPinned
				? "hsla(var(--primary) / 0.05)"
				: "#FFFFFF",
		opacity: isPinned ? 1 : 1,
		position: isPinned ? "sticky" : "relative",
		zIndex: isPinned ? 1 : 0,
	};
};

export const ACCEPT_FILES_TYPES = [".jpg", ".jpeg", ".png", ".webp"];

// Cap for the media galleries (rooms, rates, linked rates). Arbitrary but high
// on purpose: it only exists so nobody uploads hundreds of images; the backend
// does not enforce any count.
export const MAX_MEDIA_IMAGES = 30;

export const DAYS_SELECTION = [
	{ value: "anyDay", label: "common.allDay" },
	{ value: "weekdays", label: "common.weekdaysOnly" },
	{ value: "weekends", label: "common.weekendsOnly" },
	{ value: "specificDay", label: "common.specificDays" },
];

export const getBadgeColor = (index: number) => {
	const colors = [
		"bg-green-100",
		"bg-blue-100",
		"bg-amber-100",
		"bg-indigo-100",
		"bg-fuchsia-100",
		"bg-cyan-100",
		"bg-violet-100",
		"bg-lime-100",
		"bg-sky-100",
		"bg-purple-100",
		"bg-emerald-100",
		"bg-yellow-100",
		"bg-pink-100",
		"bg-orange-100",
		"bg-teal-100",
		"bg-stone-100",
		"bg-rose-100",
	];
	return colors[index % colors.length];
};

export const navigationMenuTriggerStyle = cva(
	"group inline-flex w-full p-2 cursor-pointer items-center justify-center bg-transparent  text-sm h-full font-medium transition-colors hover:bg-accent/50 hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50 has-[a.active]:border-b-[3px] has-[a.active]:border-black",
);

export const navigationMenuItemStyle = cva(
	"group inline-flex w-full p-2 cursor-pointer items-center justify-center bg-transparent  text-sm h-full font-medium transition-colors hover:bg-accent/50 hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50 has-[a.active]:bg-accent",
);

export const STATUS_COLORS = {
	active: "bg-success",
	Active: "bg-success",
	sent: "bg-success",
	Confirmed: "bg-success",
	Completed: "bg-success",
	processed: "bg-success",
	paused: "bg-yellow-500",
	hold: "bg-yellow-500",
	Pending: "bg-yellow-500",
	pending: "bg-yellow-500",
	inProgress: "bg-yellow-500",
	Cancelled: "bg-destructive",
	Expired: "bg-destructive",
	expired: "bg-destructive",
	archived: "bg-slate-200",
};

export function formatCurrency(value: number) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(value);
}

// export function getContrastColor(hex: string) {
//   if (!hex) return '#000000'
//   const color = hex.charAt(0) === '#' ? hex.substring(1, 7) : hex
//   const r = parseInt(color.substring(0, 2), 16)
//   const g = parseInt(color.substring(2, 4), 16)
//   const b = parseInt(color.substring(4, 6), 16)
//   const uiColors = [r / 255, g / 255, b / 255]
//   const c = uiColors.map((col) => {
//     if (col <= 0.03928) {
//       return col / 12.92
//     }
//     return Math.pow((col + 0.055) / 1.055, 2.4)
//   })
//   const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
//   return L >= 0.179 ? '#000000' : '#FFFFFF'
// }
export function getPreviousMonthFromTo(): { from: Date; to: Date } {
	const currentDate = new Date();
	const firstDayPrevMonth = new Date(
		currentDate.getFullYear(),
		currentDate.getMonth() - 1,
		1,
	);
	const lastDayPrevMonth = new Date(
		currentDate.getFullYear(),
		currentDate.getMonth(),
		0,
	);

	return { from: firstDayPrevMonth, to: lastDayPrevMonth };
}

export function getStartOfMonthToToday(): { from: Date; to: Date } {
	const today = new Date();

	const from = new Date(
		Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0, 0),
	);

	return {
		from,
		to: today,
	};
}
export const getCssVariableValue = (variableName: string) => {
	const styles = getComputedStyle(document.documentElement);
	return styles.getPropertyValue(variableName).trim();
};

export function hslToRgb(hsl: string) {
	// Step 1: Parse the HSL values
	const [h, s, l] = hsl.split(" ").map((v: string, i: number) => {
		return i === 0 ? parseInt(v, 10) : parseInt(v, 10) / 100;
	});
	if (h === undefined || s === undefined || l === undefined) {
		throw new Error("Invalid HSL value");
	}
	if (h < 0 || h > 360 || s < 0 || s > 1 || l < 0 || l > 1) {
		throw new Error("Invalid HSL value");
	}

	// Step 2: Convert HSL to RGB
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;
	let r = 0,
		g = 0,
		b = 0;

	if (h >= 0 && h < 60) {
		r = c;
		g = x;
		b = 0;
	} else if (h >= 60 && h < 120) {
		r = x;
		g = c;
		b = 0;
	} else if (h >= 120 && h < 180) {
		r = 0;
		g = c;
		b = x;
	} else if (h >= 180 && h < 240) {
		r = 0;
		g = x;
		b = c;
	} else if (h >= 240 && h < 300) {
		r = x;
		g = 0;
		b = c;
	} else if (h >= 300 && h < 360) {
		r = c;
		g = 0;
		b = x;
	}

	// Convert the RGB values to the range 0-255
	r = Math.round((r + m) * 255);
	g = Math.round((g + m) * 255);
	b = Math.round((b + m) * 255);

	return `rgb(${r}, ${g}, ${b})`;
}

export const currencyList: string[] = [
	"USD",
	"EUR",
	"JPY",
	"GBP",
	"AUD",
	"CAD",
	"CHF",
	"CNY",
	"HKD",
	"NZD",
	"AED",
	"MXN",
	"BRL",
	"ARS",
	"CLP",
	"COP",
	"PEN",
];

export const dateFormatOptions = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

export const timezone: { name: string; value: string }[] = [
	{
		name: "(UTC/GMT -12:00) Eniwetok, Kwajalein",
		value: "Etc/GMT+12",
	},
	{
		name: "(UTC/GMT -11:00) Midway Island, Samoa",
		value: "Pacific/Midway",
	},
	{ name: "(UTC/GMT -10:00) Hawaii", value: "Pacific/Honolulu" },
	{ name: "(UTC/GMT -9:30) Taiohae", value: "Pacific/Marquesas" },
	{ name: "(UTC/GMT -9:00) Alaska", value: "America/Anchorage" },
	{
		name: "(UTC/GMT -8:00) Pacific Time (US, Canada)",
		value: "America/Los_Angeles",
	},
	{
		name: "(UTC/GMT -7:00) Mountain Time (US, Canada)",
		value: "America/Denver",
	},
	{
		name: "(UTC/GMT -6:00) Central Time (US, Canada), Mexico City",
		value: "America/Chicago",
	},
	{
		name: "(UTC/GMT -5:00) Eastern Time (US, Canada), Bogota, Lima",
		value: "America/New_York",
	},
	{ name: "(UTC/GMT -4:30) Caracas", value: "America/Caracas" },
	{
		name: "(UTC/GMT -4:00) Atlantic Time (Canada), Caracas, La Paz",
		value: "America/Halifax",
	},
	{
		name: "(UTC/GMT -3:30) Newfoundland",
		value: "America/St_Johns",
	},
	{
		name: "(UTC/GMT -3:00) Brazil, Buenos Aires, Georgetown",
		value: "America/Sao_Paulo",
	},
	{
		name: "(UTC/GMT -2:00) Mid-Atlantic",
		value: "America/Noronha",
	},
	{
		name: "(UTC/GMT -1:00) Azores, Cape Verde Islands",
		value: "Atlantic/Azores",
	},
	{
		name: "(UTC/GMT) Western Europe Time, London, Lisbon, Casablanca",
		value: "Europe/London",
	},
	{
		name: "(UTC/GMT +1:00) Brussels, Copenhagen, Madrid, Paris",
		value: "Europe/Paris",
	},
	{
		name: "(UTC/GMT +2:00) Kaliningrad, South Africa",
		value: "Europe/Kaliningrad",
	},
	{
		name: "(UTC/GMT +3:00) Baghdad, Riyadh, Moscow, St. Petersburg",
		value: "Europe/Moscow",
	},
	{ name: "(UTC/GMT +3:30) Tehran", value: "Asia/Tehran" },
	{
		name: "(UTC/GMT +4:00) Abu Dhabi, Muscat, Baku, Tbilisi",
		value: "Asia/Dubai",
	},
	{ name: "(UTC/GMT +4:30) Kabul", value: "Asia/Kabul" },
	{
		name: "(UTC/GMT +5:00) Ekaterinburg, Islamabad, Karachi, Tashkent",
		value: "Asia/Karachi",
	},
	{
		name: "(UTC/GMT +5:30) Bombay, Calcutta, Madras, New Delhi",
		value: "Asia/Kolkata",
	},
	{
		name: "(UTC/GMT +5:45) Kathmandu, Pokhara",
		value: "Asia/Kathmandu",
	},
	{
		name: "(UTC/GMT +6:00) Almaty, Dhaka, Colombo",
		value: "Asia/Dhaka",
	},
	{
		name: "(UTC/GMT +6:30) Yangon, Mandalay",
		value: "Asia/Yangon",
	},
	{
		name: "(UTC/GMT +7:00) Bangkok, Hanoi, Jakarta",
		value: "Asia/Bangkok",
	},
	{
		name: "(UTC/GMT +8:00) Beijing, Perth, Singapore, Hong Kong",
		value: "Asia/Shanghai",
	},
	{ name: "(UTC/GMT +8:45) Eucla", value: "Australia/Eucla" },
	{
		name: "(UTC/GMT +9:00) Tokyo, Seoul, Osaka, Sapporo, Yakutsk",
		value: "Asia/Tokyo",
	},
	{
		name: "(UTC/GMT +9:30) Adelaide, Darwin",
		value: "Australia/Adelaide",
	},
	{
		name: "(UTC/GMT +10:00) Eastern Australia, Guam, Vladivostok",
		value: "Australia/Sydney",
	},
	{
		name: "(UTC/GMT +10:30) Lord Howe Island",
		value: "Australia/Lord_Howe",
	},
	{
		name: "(UTC/GMT +11:00) Magadan, Solomon Islands, New Caledonia",
		value: "Pacific/Guadalcanal",
	},
	{
		name: "(UTC/GMT +11:30) Norfolk Island",
		value: "Pacific/Norfolk",
	},
	{
		name: "(UTC/GMT +12:00) Auckland, Wellington, Fiji, Kamchatka",
		value: "Pacific/Auckland",
	},
	{
		name: "(UTC/GMT +12:45) Chatham Islands",
		value: "Pacific/Chatham",
	},
	{
		name: "(UTC/GMT +13:00) Apia, Nukualofa",
		value: "Pacific/Apia",
	},
	{
		name: "(UTC/GMT +14:00) Line Islands, Tokelau",
		value: "Pacific/Kiritimati",
	},
];

export const fontList = [
	"Roboto",
	"Open Sans",
	"Lato",
	"Montserrat",
	"Oswald",
	"Poppins",
	"Raleway",
	"Noto Sans",
	"Merriweather",
	"Nunito Sans",
	"PT Sans",
	"Source Sans Pro",
	"Ubuntu",
	"Playfair Display",
	"Work Sans",
];

export const borderRadiusOptions = [
	{ value: 0, label: "common.borderRight" },
	{ value: 1, label: "common.borderRounded" },
];

export const nightOptions = [
	{ value: 1, label: "common.oneNight" },
	{ value: 2, label: "common.twoNights" },
	{ value: 3, label: "common.threeNights" },
	{ value: 4, label: "common.fourNights" },
	{ value: 5, label: "common.fiveNights" },
	{ value: 6, label: "common.sixNights" },
	{ value: 7, label: "common.sevenNights" },
	{ value: 8, label: "common.eightNights" },
	{ value: 9, label: "common.nineNights" },
	{ value: 10, label: "common.tenNights" },
	{ value: 11, label: "common.elevenNights" },
	{ value: 12, label: "common.twelveNights" },
	{ value: 13, label: "common.thirteenNights" },
	{ value: 14, label: "common.fourteenNights" },
	{ value: 15, label: "common.fifteenNights" },
	{ value: 21, label: "common.twentyOneNights" },
	{ value: 28, label: "common.twentyEightNights" },
];

export const BedRoomsOptions = new Array(20).fill(0);
export const BedsOptions = new Array(10).fill(0);

export const DaysIndex = [
	"common.mon",
	"common.tue",
	"common.wed",
	"common.thu",
	"common.fri",
	"common.sat",
	"common.sun",
];

export const PromoCodeTypeOptions = [
	{
		value: PromoCodeType.percentageOff,
		label: "promoCode.percentageDiscount",
	},
	{ value: PromoCodeType.fixedDiscount, label: "promoCode.fixedDiscount" },
	{ value: PromoCodeType.activeRate, label: "promoCode.activateRate" },
];

export function dateToUTC(date: string): Date {
	const isValidDate = new Date(date).toString() !== "Invalid Date";
	if (!isValidDate) {
		throw new Error("Invalid date");
	}
	const year = new Date(date).getUTCFullYear();
	const month = new Date(date).getUTCMonth();
	const day = new Date(date).getUTCDate();
	const hours = new Date(date).getUTCHours();
	const minutes = new Date(date).getUTCMinutes();
	const seconds = new Date(date).getUTCSeconds();
	const milliseconds = new Date(date).getUTCMilliseconds();

	return new Date(
		Date.UTC(year, month, day, hours, minutes, seconds, milliseconds),
	);
}

export const formatDateUTC = (date: string | Date) => {
	if (date === "") {
		return "";
	}

	let dateToFormat: Date;
	if (typeof date === "string") {
		dateToFormat = dateToUTC(date);
	} else {
		dateToFormat = date;
	}
	const year = dateToFormat.getUTCFullYear();
	const month = dateToFormat.getUTCMonth() + 1;
	const day = dateToFormat.getUTCDate();
	const parse = parseISO(
		`${year}-${month > 9 ? month : `0${month}`}-${day > 9 ? day : `0${day}`}`,
	);
	return format(parse, "yyyy-MM-dd");
};

export function get7DaysBackAndToday(): { from: Date; to: Date } {
	const today = new Date();
	const sevenDaysAgo = new Date(today);
	sevenDaysAgo.setDate(today.getDate() - 7);
	return { from: sevenDaysAgo, to: today };
}

export const AvailabilityTypeOptions = [
	{ value: AvailableType.ALWAYS, label: "activities.create.always" },
	{
		value: AvailableType["SPECIFIC RANGE"],
		label: "rates.create.specificRange",
	},
];

export const AccessRateOptions = [
	{ value: AccessRate["promotional rate"], label: "common.promotionalRate" },
	{ value: AccessRate["no restrictions"], label: "common.noRestrictions" },
	{ value: AccessRate["member rate"], label: "common.memberRate" },
];

export const LOSDiscountsTypeOptions = [
	{ value: LOSDiscountsType.percentageOff, label: "rates.create.valueNights" },
	{ value: LOSDiscountsType.nightFree, label: "rates.create.cheapestNight" },
];

export const RATE_TAGS_OPTIONS: Option[] = [
	{
		value: "Best Available Rate",
		label: "rates.tags.bestAvailableRate",
	},
	{
		value: "Special Offers",
		label: "rates.tags.specialOffers",
	},
	{
		value: "Golf",
		label: "rates.tags.golf",
	},
	{
		value: "Spa",
		label: "rates.tags.spa",
	},
	{
		value: "Fishing",
		label: "rates.tags.fishing",
	},
	{
		value: "Food",
		label: "rates.tags.food",
	},
	{
		value: "Wellness",
		label: "rates.tags.wellness",
	},
	{
		value: "Entertainment",
		label: "rates.tags.entertainment",
	},
	{
		value: "Family",
		label: "rates.tags.family",
	},
	{
		value: "Christmas",
		label: "rates.tags.christmas",
	},
	{
		value: "Wedding",
		label: "rates.tags.wedding",
	},
];

export function getOptionsFromTags(facilities: string[]): Option[] {
	const updatedOptions = [...RATE_TAGS_OPTIONS];

	for (const facility of facilities) {
		if (!updatedOptions.some((option) => option.value === facility)) {
			updatedOptions.push({ value: facility, label: facility });
		}
	}

	return updatedOptions;
}

export const FACILITIES_OPTIONS: Option[] = [
	{ value: "18 Hole Golf Course", label: "common.facilities.18HoleGolfCourse" },
	{ value: "9 Hole Golf Course", label: "common.facilities.9HoleGolfCourse" },
	{ value: "Free Parking", label: "common.facilities.freeParking" },
	{ value: "Car Parking", label: "common.facilities.carParking" },
	{
		value: "Children's Playroom",
		label: "common.facilities.childrensPlayroom",
	},
	{ value: "Elevators / Lifts", label: "common.facilities.elevatorsLifts" },
	{
		value: "Facilities for the Disabled",
		label: "common.facilities.facilitiesForDisabled",
	},
	{
		value: "Facilities for Pets",
		label: "common.facilities.facilitiesForPets",
	},
	{ value: "Games Room", label: "common.facilities.gamesRoom" },
	{ value: "Gym Facilities", label: "common.facilities.gymFacilities" },
	{
		value: "Indoor Swimming Pool",
		label: "common.facilities.indoorSwimmingPool",
	},
	{
		value: "Outdoor Swimming Pool",
		label: "common.facilities.outdoorSwimmingPool",
	},
	{ value: "Restaurant", label: "common.facilities.restaurant" },
	{ value: "Spa / Sauna", label: "common.facilities.spaSauna" },
	{ value: "Squash Courts", label: "common.facilities.squashCourts" },
	{ value: "Tennis Courts", label: "common.facilities.tennisCourts" },
	{ value: "24 Hour Front Desk", label: "common.facilities.24HourFrontDesk" },
	{ value: "Free WiFi", label: "common.facilities.freeWiFi" },
	{ value: "Family Rooms", label: "common.facilities.familyRooms" },
	{ value: "Bar", label: "common.facilities.bar" },
	{ value: "EV Charging", label: "common.facilities.evCharging" },
	{
		value: "Bicycles for Guests",
		label: "common.facilities.bicyclesForGuests",
	},
	{ value: "Kids Club", label: "common.facilities.kidsClub" },
	{
		value: "Accessible Rooms or Accommodation",
		label: "common.facilities.accessibleRoomsOrAccommodation",
	},
	{ value: "Airport Shuttle", label: "common.facilities.airportShuttle" },
	{ value: "Room Service", label: "common.facilities.roomService" },
];

export function getOptionsFromFacilities(facilities: string[]): Option[] {
	const updatedOptions = [...FACILITIES_OPTIONS];

	for (const facility of facilities) {
		if (!updatedOptions.some((option) => option.value === facility)) {
			updatedOptions.push({ value: facility, label: facility });
		}
	}

	return updatedOptions;
}
export const ROOMS_FACILITIES_OPTIONS: Option[] = [
	{ value: "Television", label: "rooms.television" },
	{
		value: "Direct Dial Telephone",
		label: "rooms.directDialTelephone",
	},
	{
		value: "Iron and Ironing Board",
		label: "rooms.ironAndIroningBoard",
	},
	{ value: "Refrigerator", label: "rooms.refrigerator" },
	{ value: "Mini Bar", label: "rooms.miniBar" },
	{
		value: "Tea / Coffee Facilities",
		label: "rooms.teaCoffeeFacilities",
	},
	{ value: "Espresso Machine", label: "rooms.espressoMachine" },
	{
		value: "110/220 Volt Adapters",
		label: "rooms.voltAdapters",
	},
	{ value: "Air Conditioning", label: "rooms.airConditioning" },
	{ value: "Balcony", label: "rooms.balcony" },
	{ value: "Wi-Fi", label: "rooms.wifi" },
	{ value: "Vanity Unit", label: "rooms.vanityUnit" },
	{ value: "Hairdryer", label: "rooms.hairdryer" },
	{ value: "Jacuzzi Bath", label: "rooms.jacuzziBath" },
	{ value: "Bath Tub", label: "rooms.bathTub" },
	{ value: "Bath with Shower", label: "rooms.bathWithShower" },
	{ value: "Night Safe", label: "rooms.nightSafe" },
	{
		value: "Robe and Slippers",
		label: "rooms.robeAndSlippers",
	},
	{
		value: "Separate Sitting Room",
		label: "rooms.separateSittingRoom",
	},
	{ value: "Work Station", label: "rooms.workStation" },
	{ value: "Sea View", label: "rooms.seaView" },
	{ value: "Lake View", label: "rooms.lakeView" },
	{ value: "Garden View", label: "rooms.gardenView" },
	{ value: "Mountain View", label: "rooms.mountainView" },
	{ value: "Disabled Access", label: "rooms.disabledAccess" },
	{ value: "Cooker", label: "rooms.cooker" },
	{ value: "Dishwasher", label: "rooms.dishwasher" },
	{ value: "Washing Machine", label: "rooms.washingMachine" },
	{ value: "Kitchenette", label: "rooms.kitchenette" },
	{
		value: "Microwave & Grill Combi",
		label: "rooms.microwaveGrillCombi",
	},
	{
		value: "USB Charging Points",
		label: "rooms.usbChargingPoints",
	},
];

export function getOptionsFromRoomFacilities(facilities: string[]): Option[] {
	const updatedOptions = [...ROOMS_FACILITIES_OPTIONS];

	for (const facility of facilities) {
		if (!updatedOptions.some((option) => option.value === facility)) {
			updatedOptions.push({ value: facility, label: facility });
		}
	}

	return updatedOptions;
}

// Custom facilities are free text; without this the same facility typed with
// different casing is stored twice and shows up twice in the booking engine.
export function normalizeCustomFacility(
	input: string,
	options: Option[],
): string {
	const trimmed = input.trim().replace(/\s+/g, " ");
	const existing = options.find(
		(option) => option.value.toLowerCase() === trimmed.toLowerCase(),
	);
	if (existing) return existing.value;
	return trimmed
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

export const getUniqueRooms = (rooms: SpecificDaysRate[]) => {
	const uniqueRooms: SpecificDaysRate[] = [];
	rooms.reduce(
		(acc: Record<string, SpecificDaysRate>, room) => {
			if (room.roomId && !acc[room.roomId]) {
				acc[room.roomId] = room;
				uniqueRooms.push(room);
			}

			return acc;
		},

		{},
	);

	return uniqueRooms;
};

export const DAYS_TO_CHECK = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
	"Sunday",
] as const;

export const preferredLanguageOptions = [
	{ value: "EN", label: "English" },
	{ value: "ES", label: "Español" },
	{ value: "PT-BR", label: "Português (Brasil)" },
];

export const DepositsOptions = [
	{ value: Deposits["Take a fixed amount"], label: "common.takeFixedAmount" },
	{ value: Deposits["Take a percentage"], label: "common.takePercentage" },
	{ value: Deposits["Take entire amount"], label: "common.takeEntireAmount" },
	{ value: Deposits["Take no deposit"], label: "common.takeNoDeposit" },
	{
		value: Deposits["Take value of first night"],
		label: "common.takeValueOfFirstNight",
	},
];

export const ContactFormCategoryOptions = [
	{ value: ContactFormCategory.Properties, label: "helpAndSupport.properties" },
	{ value: ContactFormCategory.Rooms, label: "helpAndSupport.rooms" },
	{ value: ContactFormCategory.Rates, label: "helpAndSupport.rates" },
	{ value: ContactFormCategory.Packages, label: "helpAndSupport.packages" },
	{
		value: ContactFormCategory["Activities & Products"],
		label: "helpAndSupport.activitiesAndProducts",
	},
	{
		value: ContactFormCategory["Add-ons"],
		label: "helpAndSupport.addons",
	},
	{
		value: ContactFormCategory["Cancellation and Tax Policies"],
		label: "helpAndSupport.cancellationAndTaxPolicies",
	},
	{ value: ContactFormCategory.DNS, label: "helpAndSupport.dns" },
	{
		value: ContactFormCategory["Payment Integration"],
		label: "helpAndSupport.paymentIntegration",
	},
];

export const calculateDiscountedPrice = (
	originalPrice: number,
	discountPercentage: number,
): number => {
	if (discountPercentage === 0) {
		return Number(originalPrice.toFixed(2));
	}
	if (discountPercentage < 0 || discountPercentage > 100) {
		throw new Error("discountPercentage must be between 0 and 100");
	}

	return Number(
		(originalPrice - (originalPrice * discountPercentage) / 100).toFixed(2),
	);
};

export const toCamelCase = (str: string) => {
	return str.charAt(0).toLowerCase() + str.slice(1);
};

export const getDomain = (domain: string) => {
	try {
		const hostname = new URL(domain).hostname;
		const parsedDomain = parseDomain(hostname) ?? "";
		return `.${parsedDomain}`;
	} catch {
		return "";
	}
};

export const copyToClipboard = async (text: string) => {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
};

export const getCheckInDateRate = (rate: Rate) => {
	const isAlways = rate.rateDetails.availableType === AvailableType.ALWAYS;
	if (isAlways) {
		return format(addMonths(new Date(), 6), "yyyy-MM-dd");
	} else {
		const startDate = dateToUTC(rate.rateDetails.availableFrom);
		const endDate = dateToUTC(rate.rateDetails.availableTo);
		const difference = differenceInCalendarMonths(endDate, startDate);
		const resultDate = addMonths(startDate, difference - 1);
		return formatDateUTC(resultDate);
	}
};

export const getCheckOutDateRate = (checkInDate: string, rate: Rate) => {
	const minLos = rate.limitations.minimumLengthOfStay;
	return formatDateUTC(addDays(dateToUTC(checkInDate), minLos + 1));
};

export function HEXToHSL(color?: string | null) {
	// Guard: un color ausente/invalido no debe tirar abajo toda la app.
	// (p. ej. si el branding por dominio no devuelve algun color)
	if (typeof color !== "string" || (color.length !== 4 && color.length !== 7)) {
		return "";
	}
	let r = 0,
		g = 0,
		b = 0;
	// 3 digits
	if (color.length === 4) {
		r = parseInt(color[1] + color[1], 16);
		g = parseInt(color[2] + color[2], 16);
		b = parseInt(color[3] + color[3], 16);
		// 6 digits
	} else if (color.length === 7) {
		r = parseInt(color[1] + color[2], 16);
		g = parseInt(color[3] + color[4], 16);
		b = parseInt(color[5] + color[6], 16);
	}
	r /= 255;
	g /= 255;
	b /= 255;
	const max = Math.max(r, g, b),
		min = Math.min(r, g, b);
	let h = (max + min) / 2,
		s = (max + min) / 2;
	const l = (max + min) / 2;

	if (max === min) {
		h = s = 0; // achromatic
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}
	return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
export const CancellationReasonOptions = [
	{ value: CancellationReason.Other, label: "reservation.other" },
	{
		value: CancellationReason.ConfirmationMissed,
		label: "reservation.confirmationMissed",
	},
	{
		value: CancellationReason.BookedElsewhere,
		label: "reservation.bookedElsewhere",
	},
	{
		value: CancellationReason.ForceMajeure,
		label: "reservation.forceMajeure",
	},
	{
		value: CancellationReason.GuestComplaint,
		label: "reservation.guestComplaint",
	},
	{ value: CancellationReason.NoShow, label: "reservation.noShow" },
	{
		value: CancellationReason.PriceTooHigh,
		label: "reservation.priceTooHigh",
	},
	{
		value: CancellationReason.ServiceNotAvailable,
		label: "reservation.serviceNotAvailable",
	},
	{ value: CancellationReason.InputError, label: "reservation.inputError" },
	{
		value: CancellationReason.InvalidPayment,
		label: "reservation.invalidPayment",
	},
	{
		value: CancellationReason.TravelAgency,
		label: "reservation.travelAgency",
	},
	{
		value: CancellationReason.RequestedByGuest,
		label: "reservation.requestedByGuest",
	},
	{ value: CancellationReason.Update, label: "reservation.update" },
	{
		value: CancellationReason.BookingAbandoned,
		label: "reservation.bookingAbandoned",
	},
	{
		value: CancellationReason.RequestedByBooker,
		label: "reservation.requestedByBooker",
	},
];

export const cardsImages = {
	[AcceptedCards.Visa]: "/img/credit-cards/visa.svg",
	[AcceptedCards.MasterCard]: "/img/credit-cards/mastercard.svg",
	[AcceptedCards["American Express"]]: "/img/credit-cards/american-express.svg",
	[AcceptedCards.Discover]: "/img/credit-cards/discover.svg",
	[AcceptedCards.JCB]: "/img/credit-cards/jcb.svg",
	[AcceptedCards["Diner's Club"]]: "/img/credit-cards/diners-club.svg",
	[AcceptedCards.BC]: "/img/credit-cards/bc.svg",
	[AcceptedCards.Dankort]: "/img/credit-cards/dankort.svg",
	[AcceptedCards.Delta]: "/img/credit-cards/delta.svg",
	[AcceptedCards.Electron]: "/img/credit-cards/visa-electron.svg",
	[AcceptedCards.Elo]: "/img/credit-cards/elo.svg",
	[AcceptedCards.enRoute]: "/img/credit-cards/enroute.svg",
	[AcceptedCards["Euro Card"]]: "/img/credit-cards/eurocard.svg",
	[AcceptedCards.Hipercard]: "/img/credit-cards/hipercard.svg",
	[AcceptedCards.Maestro]: "/img/credit-cards/maestro.svg",
	[AcceptedCards.Solo]: "/img/credit-cards/solo.svg",
	[AcceptedCards.Switch]: "/img/credit-cards/switch.svg",
	[AcceptedCards["Union Pay"]]: "/img/credit-cards/unionpay.svg",
};

export const originOptions = [
	{ value: "All", label: "common.all" },
	{ value: Origin.Web, label: "Web" },
	{ value: Origin.Mews, label: "Mews" },
];

export function getDayOfWeekKey(dateString: string): string {
	const [year, month, day] = dateString.split("-").map(Number);
	const date = new Date(year, month - 1, day);
	const dayIndex = date.getDay();

	const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
	return days[dayIndex];
}

export function getContrastTextColor(
	color: string,
): "text-white" | "text-black" {
	let r = 0,
		g = 0,
		b = 0;

	// Si el color viene con #, lo procesamos como hex
	if (color.startsWith("#")) {
		// 3 digits
		if (color.length === 4) {
			r = parseInt(color[1] + color[1], 16);
			g = parseInt(color[2] + color[2], 16);
			b = parseInt(color[3] + color[3], 16);
			// 6 digits
		} else if (color.length === 7) {
			r = parseInt(color[1] + color[2], 16);
			g = parseInt(color[3] + color[4], 16);
			b = parseInt(color[5] + color[6], 16);
		}
	} else if (color.startsWith("rgb")) {
		// Si viene en formato rgb(r, g, b)
		const match = color.match(/\d+/g);
		if (match) {
			r = parseInt(match[0], 10);
			g = parseInt(match[1], 10);
			b = parseInt(match[2], 10);
		}
	} else {
		// Si viene sin # asumimos que es hex
		if (color.length === 3) {
			r = parseInt(color[0] + color[0], 16);
			g = parseInt(color[1] + color[1], 16);
			b = parseInt(color[2] + color[2], 16);
		} else if (color.length === 6) {
			r = parseInt(color[0] + color[1], 16);
			g = parseInt(color[2] + color[3], 16);
			b = parseInt(color[4] + color[5], 16);
		}
	}

	// Calcular luminancia relativa según WCAG
	// https://www.w3.org/TR/WCAG20/#relativeluminancedef
	const rsRGB = r / 255;
	const gsRGB = g / 255;
	const bsRGB = b / 255;

	const rLinear =
		rsRGB <= 0.03928 ? rsRGB / 12.92 : ((rsRGB + 0.055) / 1.055) ** 2.4;
	const gLinear =
		gsRGB <= 0.03928 ? gsRGB / 12.92 : ((gsRGB + 0.055) / 1.055) ** 2.4;
	const bLinear =
		bsRGB <= 0.03928 ? bsRGB / 12.92 : ((bsRGB + 0.055) / 1.055) ** 2.4;

	const luminance = 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;

	// Si la luminancia es mayor que 0.5, usar texto negro, sino texto blanco
	return luminance > 0.5 ? "text-black" : "text-white";
}

/**
 * Safely extracts the API error message from a wretch error.
 *
 * The codebase-wide pattern `JSON.parse(error.message)` throws whenever the
 * backend responds with a non-JSON body (500 HTML page, plain text, network
 * failure), which swallows the toast entirely and leaves the admin with no
 * feedback. This helper never throws.
 */
export function parseApiErrorMessage(
	error: unknown,
	fallback = "Something went wrong",
): string {
	if (!(error instanceof Error) || !error.message) return fallback;
	try {
		const parsed = JSON.parse(error.message) as { message?: unknown };
		return typeof parsed?.message === "string" && parsed.message
			? parsed.message
			: fallback;
	} catch {
		// Non-JSON body: only surface it when it's a short plain-text message —
		// a 500 HTML page dumped verbatim into a toast is worse than the fallback.
		const text = error.message.trim();
		return text && !text.startsWith("<") && text.length <= 200
			? text
			: fallback;
	}
}

/**
 * The machine-readable part of an API error: its HTTP status (wretch sets `status`), the stable
 * `code` payment-service and property-service send, and the field paths of a validation-style error
 * (`data.fields`). Never throws; every part is optional because a network failure has none.
 */
export function parseApiError(error: unknown): {
	status?: number;
	code?: string;
	fields: string[];
} {
	const status =
		error && typeof error === "object" && typeof (error as { status?: unknown }).status === "number"
			? (error as { status: number }).status
			: undefined;
	if (!(error instanceof Error) || !error.message) return { status, fields: [] };
	try {
		const parsed = JSON.parse(error.message) as {
			code?: unknown;
			data?: { fields?: unknown } | null;
		};
		const fields = Array.isArray(parsed?.data?.fields)
			? parsed.data.fields.filter((f): f is string => typeof f === "string")
			: [];
		return { status, code: typeof parsed?.code === "string" ? parsed.code : undefined, fields };
	} catch {
		return { status, fields: [] };
	}
}
